import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

// Creatable dropdown: click to open the full list (Brookfield, Hemingray…),
// filter by typing, pick an option, OR type a brand-new value.
export function ComboInput({ label, value, onChange, options = [], placeholder = "", testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // When open, filter by what the user has typed since opening; otherwise show all.
  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openList = () => { setQuery(""); setActive(0); setOpen(true); };

  const choose = (opt) => {
    onChange(opt);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { openList(); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[active]) choose(filtered[active]); else setOpen(false); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">{label}</label>}
      <div className="relative">
        <input
          data-testid={testId}
          value={open ? query : (value || "")}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); if (!open) setOpen(true); setActive(0); }}
          onFocus={openList}
          onClick={openList}
          onKeyDown={onKeyDown}
          placeholder={value ? value : (placeholder || "Select or type…")}
          autoComplete="off"
          className="w-full surface border rounded-lg pl-3 pr-9 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <button
          type="button"
          tabIndex={-1}
          data-testid={testId ? `${testId}-toggle` : undefined}
          onMouseDown={(e) => { e.preventDefault(); open ? setOpen(false) : openList(); }}
          className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-page"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div
          ref={listRef}
          data-testid={testId ? `${testId}-list` : undefined}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border surface shadow-lg py-1"
          style={{ borderColor: "var(--page-border)" }}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-page">
              {query ? <>No match — press Enter to add “{query}”</> : "No options yet — type to add one"}
            </div>
          ) : (
            filtered.map((o, i) => (
              <button
                type="button"
                key={o}
                data-testid={testId ? `${testId}-option-${o}` : undefined}
                onMouseDown={(e) => { e.preventDefault(); choose(o); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${i === active ? "bg-amber-500/15" : ""}`}
              >
                <span className="truncate">{o}</span>
                {value === o && <Check className="w-4 h-4 brand-text shrink-0" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
